import { useState, useEffect, useMemo } from "react";
import { ShoppingCart, Loader2 } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { useCheckoutStore } from "../stores/checkout-store";
import {
  useCheckout,
  useCheckoutParticipants,
  useRemoveCheckoutItem,
  isRecoverableCheckoutSessionError,
} from "../lib/basket-api";
import { Link } from "react-router-dom";
import {
  handleApiError,
  showErrorToast,
  showSuccessToast,
} from "../lib/error-handling";
import { ParticipantList } from "../components/ParticipantList";
import { EventGroupCard } from "../components/basket/EventGroupCard";
import { CheckoutSummary } from "../components/basket/CheckoutSummary";
import { ParticipantEditModal } from "../components/basket/ParticipantEditModal";
import { useGroupedBasketItems } from "../lib/hooks/useGroupedBasketItems";
import { useBasketCheckout } from "../lib/hooks/useBasketCheckout";
import { useParticipantEditing } from "../lib/hooks/useParticipantEditing";
import type {
  CheckoutItem,
  Event,
} from "../lib/types";
import { useUser, fetchClient } from "@/lib/api-client";
import { parseISO } from "date-fns";

export const Basket = () => {
  const { data: user } = useUser();
  const { checkoutId } = useCheckoutStore();
  const [bookingEmailAddress, setBookingEmailAddress] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);

  useEffect(() => {
    if (user) {
      if (user.email && !bookingEmailAddress) {
        setBookingEmailAddress(user.email);
      }
      if (user.marketingConsent !== undefined && user.marketingConsent !== null) {
        setMarketingConsent(user.marketingConsent);
      }
    }
  }, [user, bookingEmailAddress]);

  const {
    data: checkoutResponse,
    isLoading: isCheckoutLoading,
    error: checkoutError,
    refetch: refetchCheckout,
  } = useCheckout(checkoutId);

  const {
    data: participantsResponse,
    isLoading: isParticipantsLoading,
    refetch: refetchParticipants,
  } = useCheckoutParticipants(checkoutId);

  const checkout = checkoutResponse?.checkout;
  const basketItems = checkout?.items || [];
  const participants = participantsResponse?.participants ?? [];
  const canComplete = participantsResponse?.canComplete ?? false;

  const removeCheckoutItem = useRemoveCheckoutItem();

  const calculateTotal = () => checkout?.totalCents || 0;

  const depositInfo = useMemo(() => {
    if (!basketItems.length) return null;

    let totalDeposit = 0;
    let anyDepositInFuture = false;
    const balancesByDate = new Map<string, number>();
    const now = new Date();

    basketItems.forEach((item: CheckoutItem) => {
      const depositCents = item.depositCents ?? null;
      const balanceDueAt = item.balanceDueAt
        ? parseISO(item.balanceDueAt)
        : null;

      if (
        depositCents !== null &&
        depositCents !== undefined &&
        balanceDueAt &&
        balanceDueAt > now
      ) {
        totalDeposit += depositCents;
        anyDepositInFuture = true;

        const balanceAmount = item.unitPriceCents - depositCents;
        if (balanceAmount > 0) {
          const dateKey = balanceDueAt.toISOString();
          balancesByDate.set(
            dateKey,
            (balancesByDate.get(dateKey) || 0) + balanceAmount,
          );
        }
      } else {
        totalDeposit += item.unitPriceCents;
      }
    });

    if (!anyDepositInFuture) return null;

    const sortedBalances = Array.from(balancesByDate.entries())
      .map(([dateStr, amount]) => ({
        date: parseISO(dateStr),
        amount,
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    return {
      totalDeposit,
      balances: sortedBalances,
    };
  }, [basketItems, checkout?.totalCents]);

  const getTotalEvents = () => {
    const uniqueEventIds = new Set(
      basketItems.flatMap((item: CheckoutItem) => {
        const eventId = item.eventItem?.eventId;
        const raceEventId = (item as { raceItem?: { eventId: string } }).raceItem?.eventId;
        return [eventId, raceEventId].filter(Boolean) as string[];
      }),
    );
    return uniqueEventIds.size;
  };

  const [eventMap, setEventMap] = useState<Record<string, Event>>({});

  useEffect(() => {
    const uniqueEventIds = Array.from(
      new Set(
        basketItems.flatMap((item: CheckoutItem) => {
          const eventId = item.eventItem?.eventId;
          const raceEventId = (item as { raceItem?: { eventId: string } }).raceItem?.eventId;
          return [eventId, raceEventId].filter(Boolean) as string[];
        }),
      ),
    );

    const fetchEvents = async () => {
      const newEventMap: Record<string, Event> = {};

      for (const eventId of uniqueEventIds) {
        if (!eventId) continue;
        try {
          const response = await fetchClient.GET("/api/public/events/{id}", {
            params: { path: { id: eventId } },
          });
          if (response.data?.event) {
            newEventMap[eventId] = response.data.event;
          }
        } catch {
          // Failed to fetch event, continue without it
        }
      }

      setEventMap(newEventMap);
    };

    if (uniqueEventIds.length > 0) {
      fetchEvents();
    }
  }, [basketItems]);

  const groupedBasketItems = useGroupedBasketItems(basketItems, eventMap);

  const {
    editingParticipant,
    handleEditParticipant,
    handleCloseEditModal,
    handleStepSave,
    handleConsentFormComplete,
  } = useParticipantEditing(checkoutId, refetchParticipants);

  const { handleProceedToCheckout, isProcessing } = useBasketCheckout(checkoutId);

  const handleRemoveItem = async (itemId: string) => {
    if (!checkoutId) return;

    try {
      await removeCheckoutItem.mutateAsync({
        params: { path: { checkoutId, itemId } },
      });
      refetchCheckout();
      refetchParticipants();
      showSuccessToast("Item removed from basket");
    } catch (error) {
      if (!isRecoverableCheckoutSessionError(error)) {
        const apiError = handleApiError(error);
        showErrorToast(apiError);
      }
    }
  };

  const handleProceedToCheckoutWithParams = () => {
    const total = calculateTotal();
    handleProceedToCheckout(total, bookingEmailAddress, marketingConsent);
  };

  const total = calculateTotal();
  const totalParticipants = participants.length;
  const isLoading = isCheckoutLoading || isParticipantsLoading;
  const error = checkoutError;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-gray-600">Loading basket...</span>
      </div>
    );
  }

  if (error) {
    if (isRecoverableCheckoutSessionError(error)) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-2 text-gray-600">Loading basket...</span>
        </div>
      );
    }

    const apiError = handleApiError(error);

    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Error loading basket
        </h3>
        <p className="text-gray-600 mb-4">
          {apiError.message || "There was a problem loading your basket."}
        </p>
        <Button
          onClick={() => {
            refetchCheckout();
            refetchParticipants();
          }}
        >
          Try again
        </Button>
      </div>
    );
  }

  if (!checkoutId || basketItems.length === 0) {
    return (
      <div className="space-y-8">
        <div className="flex items-center gap-3">
          <ShoppingCart className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Basket</h1>
            <p className="text-gray-600">Your basket is empty</p>
          </div>
        </div>

        <Card>
          <div className="text-center py-12">
            <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Your basket is empty
            </h3>
            <p className="text-gray-600 mb-4">
              Add some events to get started with your booking.
            </p>
            <Button asChild>
              <Link to="/events">Browse Events</Link>
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3">
        <ShoppingCart className="w-8 h-8 text-primary-600" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Basket</h1>
          <p className="text-gray-600">
            {totalParticipants} attendees across {getTotalEvents()} events
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Events & Attendees
          </h2>
          <div className="space-y-6">
            {groupedBasketItems.map(([eventId, group]) => (
              <EventGroupCard
                key={eventId}
                eventId={eventId}
                event={group.event}
                items={group.items}
                totalPrice={group.totalPrice}
                currency={checkout?.currency || "gbp"}
                onRemoveAll={() => {
                  group.items.forEach((item) => handleRemoveItem(item.id));
                }}
                onRemoveItem={handleRemoveItem}
              />
            ))}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-4 space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                Consent Forms
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Complete consent forms for each participant. Forms will be saved
                to all events the participant is attending.
              </p>
              <ParticipantList
                participants={participants}
                onEditParticipant={handleEditParticipant}
              />
            </div>

            <CheckoutSummary
              totalEvents={getTotalEvents()}
              totalParticipants={totalParticipants}
              total={total}
              currency={checkout?.currency || "gbp"}
              depositInfo={depositInfo}
              allParticipantsComplete={canComplete}
              participants={participants}
              user={user ? { email: user.email ?? null } : null}
              bookingEmailAddress={bookingEmailAddress}
              marketingConsent={marketingConsent}
              onBookingEmailChange={setBookingEmailAddress}
              onMarketingConsentChange={setMarketingConsent}
              onProceedToCheckout={handleProceedToCheckoutWithParams}
              isProcessing={isProcessing}
            />
          </div>
        </div>
      </div>

      {editingParticipant && (
        <ParticipantEditModal
          participant={editingParticipant}
          onStepSave={handleStepSave}
          onComplete={handleConsentFormComplete}
          onCancel={handleCloseEditModal}
        />
      )}
    </div>
  );
};
